import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { Pool as PoolIcon, Add as AddIcon } from '@mui/icons-material'
import { usePools } from '../../hooks/usePools'
import PoolCard from './PoolCard'
import PoolConfig from './PoolConfig'

const PoolList = ({ selectedPool, onPoolSelect }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { pools, loading, createPool, updatePool } = usePools()
  const [showConfig, setShowConfig] = useState(false)
  const [editingPool, setEditingPool] = useState(null)

  const handlePoolSelect = (pool) => {
    // Set the selected pool in context
    onPoolSelect(pool)
    // Navigate to dashboard
    navigate('/dashboard')
  }

  const handleCreatePool = () => {
    setEditingPool(null)
    setShowConfig(true)
  }

  const handleEditPool = (pool) => {
    setEditingPool(pool)
    setShowConfig(true)
  }

  const handleSavePool = async (poolData) => {
    if (editingPool) {
      return await updatePool(editingPool.id, poolData)
    } else {
      return await createPool(poolData)
    }
  }

  const handleCancel = () => {
    setShowConfig(false)
    setEditingPool(null)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Pool Creation Form - Always visible at top */}
      {showConfig && (
        <Box sx={{ mb: 4 }}>
          <PoolConfig
            pool={editingPool}
            onSave={handleSavePool}
            onCancel={handleCancel}
          />
        </Box>
      )}

      {/* Add Pool Button - Only show when not in config mode */}
      {!showConfig && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePool}
            size="large"
            fullWidth={isMobile}
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1.1rem',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Create New Pool
          </Button>
        </Box>
      )}

      {/* Pool List Header */}
      {pools.length > 0 && !showConfig && (
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            component="h2" 
            sx={{ 
              fontWeight: 600,
              mb: 1,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Your Pools
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a pool to view measurements and manage chemistry
          </Typography>
        </Box>
      )}

      {/* Pool List or Empty State */}
      {pools.length === 0 && !showConfig ? (
        <Box sx={{ textAlign: 'center', py: { xs: 6, md: 12 } }}>
          <PoolIcon
            sx={{
              fontSize: { xs: 48, md: 64 },
              color: 'text.disabled',
              mb: 2
            }}
          />
          <Typography variant="h6" component="h3" gutterBottom>
            No pools yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first pool to start tracking chemistry measurements.
          </Typography>
        </Box>
      ) : pools.length > 0 && !showConfig ? (
        <Grid container spacing={3}>
          {pools.map((pool) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={pool.id}>
              <PoolCard
                pool={pool}
                isSelected={selectedPool?.id === pool.id}
                onSelect={handlePoolSelect}
                onEdit={handleEditPool}
              />
            </Grid>
          ))}
        </Grid>
      ) : null}
    </Box>
  )
}

export default PoolList